import React from 'react';
import { t } from '../lib/i18n';
import MiniSparkline from './MiniSparkline';

/**
 * AccountantTrendTable -- Per-accountant trend data with sparklines.
 *
 * Props:
 *   accountantTrends: { [name: string]: Array<{ label: string, avgScore: number }> }
 *
 * Shows up to 6 most recent period columns with score coloring,
 * a delta column, and a sparkline per accountant row.
 */
export default function AccountantTrendTable({ accountantTrends }) {
    const entries = Object.entries(accountantTrends).filter(([name, data]) => {
        const key = String(name || '').trim().toLowerCase();
        return data.length >= 1 && !['unassigned', 'nieprzypisane', 'nieznany', 'ukjent'].includes(key);
    });
    if (entries.length === 0) {
        return <p className="text-sm text-slate-400 text-center py-4">{t('reports_trend_no_history')}</p>;
    }

    // Get unique period labels in order
    const allLabels = [];
    entries.forEach(([, data]) =>
        data.forEach(d => {
            if (!allLabels.includes(d.label)) allLabels.push(d.label);
        })
    );
    const visibleLabels = allLabels.slice(-6);

    // Sort by best improvement (delta)
    const sorted = entries
        .map(([name, data]) => {
            const first = data[0].avgScore;
            const last = data[data.length - 1].avgScore;
            return { name, data, delta: data.length >= 2 ? +(last - first).toFixed(1) : null, lastScore: last };
        })
        .sort((a, b) => {
            if (a.delta !== null && b.delta !== null && a.delta !== b.delta) return b.delta - a.delta;
            return (b.lastScore || 0) - (a.lastScore || 0);
        });

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-700">
                        <th className="text-left py-2 px-2 font-medium">{t('reports_accountant')}</th>
                        {visibleLabels.map(l => (
                            <th key={l} className="text-center py-2 px-2 font-medium">{l}</th>
                        ))}
                        <th className="text-center py-2 px-2 font-medium">{t('reports_trend_delta')}</th>
                        <th className="text-center py-2 px-2 font-medium">Trend</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map(({ name, data, delta }) => {
                        const dataMap = {};
                        data.forEach(d => { dataMap[d.label] = d; });
                        return (
                            <tr key={name} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                <td className="py-2 px-2 dark:text-white font-medium truncate max-w-[120px]">{name}</td>
                                {visibleLabels.map(l => {
                                    const d = dataMap[l];
                                    if (!d) {
                                        return <td key={l} className="py-2 px-2 text-center text-slate-300 dark:text-slate-600">&mdash;</td>;
                                    }
                                    const sc = d.avgScore;
                                    const cls = sc >= 7 ? 'text-emerald-500' : sc >= 4 ? 'text-amber-500' : 'text-red-500';
                                    return <td key={l} className={`py-2 px-2 text-center font-mono font-bold ${cls}`}>{sc}</td>;
                                })}
                                <td className={`py-2 px-2 text-center font-mono font-bold ${delta === null ? 'text-slate-400' : delta >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                    {delta === null ? '—' : `${delta >= 0 ? '+' : ''}${delta}`}
                                </td>
                                <td className="py-2 px-2 text-center">
                                    <MiniSparkline data={data} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
